/**
 * Which engine each Lease grid column resolved to, or that it cannot be priced at all.
 *
 * This is where Lease diverges from Finance. Finance sends a no-program column to user-program-quotes and estimates a
 * payment for it; Lease must not, since a lease payment is driven by the residual and substituting one the column was
 * never quoted at is calculating with bad data. So a no-program Lease column resolves in one pass with no pricing call,
 * and the one way out is the user typing a rate of their own.
 *
 * Deliberately free of Redux, like `paymentGridSourceUtils`: callers pass in the facts these rules depend on.
 */
import { PaymentGridSource } from './paymentGridSourceUtils';

/** The facts a column's engine is decided from. Every one is read from current state, never carried forward. */
export type LeaseColumnFacts = {
    termMonthsList: (number | null)[];
    /** The deal's own term, which is the only column the deal's own engine decides. */
    currentTermMonths?: number;
    /**
     * Whether the deal itself is on user-program-quotes, i.e. Auto Select Program is off. Governs the deal's own
     * column alone: every other column starts on the program resolved for its term (US2118720).
     */
    isUserProgramQuotes: boolean;
    isDealOnUserProgramQuotes?: boolean;
    isDealsOwnColumnBeingReTermed?: boolean;
    /** Whether a sell rate is on record for this column, whoever wrote it. */
    hasUserSuppliedRate: (termMonths: number) => boolean;
    /**
     * Whether the user priced this column themselves *in this grid session*. Narrower than `hasUserSuppliedRate`: a
     * per-term rate also arrives from a deal-screen edit, a restore or a reload, and only a rate typed into the grid may
     * displace a column's program.
     */
    hasUserPricedColumn: (termMonths: number) => boolean;
    /** Whether a usable lender program was resolved for this term. */
    hasResolvedProgram: (termMonths: number) => boolean;
    /**
     * The verdict this column already carries. Read only by the Auto Select Program **off** branch, and only to keep a
     * column already recorded `programQuotes`: an untick is not a rebuild, so its pinned program and engine survive.
     * Not read on the ASP-on branch, where the program is the authority, and it does not survive a close.
     */
    recordedSource?: (termMonths: number) => PaymentGridSource | undefined;
    isDealsOwnColumn?: (termMonths: number) => boolean;
};

export type ResolveLeaseColumnSourcesArgs = LeaseColumnFacts & {
    /** Whether every cell in this column came back with a payment. */
    isColumnFullyPriced: (termMonths: number) => boolean;
};

const isGridTerm = (termMonths: number | null): termMonths is number => typeof termMonths === 'number' && termMonths > 0;

/**
 * The engine a column would be priced on, or `undefined` for a column nothing can price - which is the "no call
 * is made for it" case, not a failure to be retried.
 */
const intendedSource = (termMonths: number, facts: LeaseColumnFacts): PaymentGridSource | undefined => {
    const {
        currentTermMonths,
        isUserProgramQuotes,
        isDealOnUserProgramQuotes = false,
        isDealsOwnColumnBeingReTermed = false,
        hasUserSuppliedRate,
        hasUserPricedColumn,
        hasResolvedProgram,
        recordedSource,
        isDealsOwnColumn
    } = facts;

    // With no term selected there is nothing to identify the deal's own column by, so on a deal priced outside its
    // programs every column is priced rather than none.
    if (isUserProgramQuotes && !isGridTerm(currentTermMonths ?? null)) return PaymentGridSource.UserProgramQuotes;

    /**
     * US2118720: the deal's engine decides the deal's OWN column and nothing else. Every other column starts on the
     * program resolved for its term, whatever the deal is priced on - so an Auto-Select-off grid still shows what the
     * other terms carry instead of leaving them blank (Lease) or estimating every one of them off the deal's own rate
     * (Finance). The deal's own column is unchanged: it follows the deal, as the checkbox says it does.
     */
    const dealEngineDecides =
        isUserProgramQuotes || (isDealOnUserProgramQuotes && !(isDealsOwnColumnBeingReTermed && hasResolvedProgram(termMonths)));
    if (dealEngineDecides && (isDealsOwnColumn ? isDealsOwnColumn(termMonths) : termMonths === currentTermMonths)) {
        // A rate the user typed into THIS column outranks everything below. The narrow predicate, deliberately:
        // `hasUserSuppliedRate` is also true for a per-term rate a response seeded.
        if (hasUserPricedColumn(termMonths)) return PaymentGridSource.UserProgramQuotes;

        // A column the grid already priced against its own pinned program keeps it - unticking ASP re-prices nothing and
        // drops no pin, which is what lets a click on it price on program-quotes and re-tick the checkbox.
        if (recordedSource?.(termMonths) === PaymentGridSource.ProgramQuotes) return PaymentGridSource.ProgramQuotes;

        // The deal prices its own column on its own engine. Both rungs this replaces answered the same for it: a rate
        // on record, or the column simply being the deal's, each meant user-program-quotes.
        return PaymentGridSource.UserProgramQuotes;
    }

    // A rate the user typed outranks the program: program-quotes resolves its own program server-side and cannot be
    // steered by a typed rate, so the service would discard it.
    if (hasUserPricedColumn(termMonths)) return PaymentGridSource.UserProgramQuotes;

    /**
     * Read only with the deal off program-quotes, which is where this rung used to sit: an untick re-prices nothing and
     * drops no pin, so a column this grid already pinned keeps it even once the live program cache has moved on. With
     * Auto Select ON the program is the authority and this is deliberately not consulted.
     */
    if (isUserProgramQuotes && recordedSource?.(termMonths) === PaymentGridSource.ProgramQuotes) {
        return PaymentGridSource.ProgramQuotes;
    }

    // Otherwise the program decides, and a rate only matters where there is no program to price against.
    if (hasResolvedProgram(termMonths)) return PaymentGridSource.ProgramQuotes;
    return hasUserSuppliedRate(termMonths) ? PaymentGridSource.UserProgramQuotes : undefined;
};

/**
 * The columns a payment can be asked for, keyed by term months. A column nothing can price is left out entirely, so no
 * call is made for it. Columns with no term are also left out: they are awaiting a term, not a payment.
 */
export const resolvePriceableColumnSources = (facts: LeaseColumnFacts): Record<number, PaymentGridSource> =>
    facts.termMonthsList.reduce<Record<number, PaymentGridSource>>((sources, termMonths) => {
        if (!isGridTerm(termMonths)) return sources;

        const intended = intendedSource(termMonths, facts);
        if (intended) sources[termMonths] = intended;
        return sources;
    }, {});

export type LeaseColumnVerdict = {
    columnSources: Record<number, PaymentGridSource>;
    refusedTerms: Record<number, true>;
    pairRejectedTerms: Record<number, true>;
};

/**
 * Every column's verdict once the rows have settled. Both "nothing could price this" and "an engine was asked and
 * returned no payment" render as `Unavailable`; `refusedTerms` keeps them apart, since `intended` is the engine the
 * column would have been asked on. `pairRejectedTerms` narrows that to a user-program-quotes refusal - a column asked
 * with the user's own rate and residual that still came back without a payment. A program-quotes column that returns
 * nothing was never given the user's figures, so it is left awaiting them rather than marked rejected.
 *
 * Whether a refusal is recorded is the caller's decision: only a scoped, answered round may record one, or a whole
 * build would claim a refusal for a column it never asked about.
 */
export const resolveLeaseColumnSources = (args: ResolveLeaseColumnSourcesArgs): LeaseColumnVerdict =>
    args.termMonthsList.reduce<LeaseColumnVerdict>(
        (verdict, termMonths) => {
            if (!isGridTerm(termMonths)) return verdict;

            const intended = intendedSource(termMonths, args);
            const isPriced = args.isColumnFullyPriced(termMonths);
            verdict.columnSources[termMonths] = intended && isPriced ? intended : PaymentGridSource.Unavailable;
            if (intended && !isPriced) verdict.refusedTerms[termMonths] = true;
            if (intended === PaymentGridSource.UserProgramQuotes && !isPriced) verdict.pairRejectedTerms[termMonths] = true;
            return verdict;
        },
        { columnSources: {}, refusedTerms: {}, pairRejectedTerms: {} }
    );

/** Whether a source means the column holds a payment the grid quoted. */
const isPriceableSource = (source?: string): boolean =>
    source === PaymentGridSource.ProgramQuotes || source === PaymentGridSource.UserProgramQuotes;

/**
 * The columns permitted to display a payment. An allow-list rather than an `Unavailable` deny-list: a close tears the
 * axes down, so on a reopen a column is *undecided*, and treating that as displayable showed stale payments.
 */
export const getQuotedTerms = (columnSources: Record<number, string>): number[] =>
    Object.keys(columnSources)
        .filter((termMonths) => isPriceableSource(columnSources[Number(termMonths)]))
        .map(Number);

export type ColumnPairFacts = {
    source?: PaymentGridSource;
    isUserProgramQuotesEngine: boolean;
    hasUserSuppliedRate: boolean;
    hasUserSuppliedResidual: boolean;
    isQuoted: boolean;
    isRefused: boolean;
};

const holdsCompletePair = (facts: ColumnPairFacts): boolean => facts.hasUserSuppliedRate && facts.hasUserSuppliedResidual;

export const isColumnPairIncomplete = (facts: ColumnPairFacts): boolean =>
    facts.isUserProgramQuotesEngine && !facts.isQuoted && !holdsCompletePair(facts);

export const isColumnAwaitingPair = (facts: ColumnPairFacts): boolean => isColumnPairIncomplete(facts) && !facts.isRefused;

export const isColumnPairRejected = (facts: ColumnPairFacts): boolean => facts.isRefused && !facts.isQuoted;
